import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Progress,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  evaluateMeasurement,
  type ResolvedItem,
  type ResolvedTemplate,
} from '@prooffleet/contracts';
import { api } from '@prooffleet/client';
import { NumberPad } from '../components/NumberPad.js';
import { PhotoCapture } from '../components/PhotoCapture.js';
import { VoiceNote } from '../components/VoiceNote.js';
import { currentLocale } from '../i18n/index.js';
import { currentPosition } from '@prooffleet/client';
import {
  enqueue,
  loadDraft,
  saveDraft,
  type InspectionDraft,
  type ItemDraft,
} from '../offline/db.js';
import { runSync } from '../offline/sync.js';

interface ChecklistPayload {
  unitId: string;
  unitNumber: string;
  resolved: ResolvedTemplate;
}

function emptyItem(itemId: string): ItemDraft {
  return {
    itemId,
    skipped: false,
    boolValue: null,
    measurementValue: null,
    note: null,
    defectReported: false,
    photoIds: [],
    audioId: null,
    capturedAt: new Date().toISOString(),
    gps: null,
  };
}

function isFilled(item: ResolvedItem, draft: ItemDraft | undefined): boolean {
  if (draft === undefined) return false;
  if (draft.skipped) return true;
  if (item.photoRequired && draft.photoIds.length === 0) return false;
  if (item.type === 'measurement') return draft.measurementValue !== null;
  if (item.type === 'boolean') return draft.boolValue !== null;
  return draft.photoIds.length > 0;
}

export function Inspection(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unitId = '' } = useParams();

  const [draft, setDraft] = useState<InspectionDraft | null>(null);
  const [index, setIndex] = useState(0);
  const [raw, setRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data } = useQuery({
    queryKey: ['checklist', unitId],
    queryFn: () => api<ChecklistPayload>(`/units/${unitId}/checklist`),
  });

  const items = useMemo(
    () => data?.resolved.sections.flatMap((section) => section.items) ?? [],
    [data],
  );

  // Черновик восстанавливается: телефон садится на восьмом пункте из четырнадцати,
  // и осмотр не должен начинаться заново.
  useEffect(() => {
    if (data === undefined) return;
    const draftId = `${unitId}:${data.resolved.templateKey}:${String(data.resolved.templateVersion)}`;

    void (async () => {
      const existing = await loadDraft(draftId);
      if (existing !== undefined) {
        setDraft(existing);
        const firstEmpty = items.findIndex((item) => !isFilled(item, existing.items[item.id]));
        setIndex(firstEmpty < 0 ? items.length - 1 : firstEmpty);
        return;
      }
      setDraft({
        draftId,
        unitId,
        unitNumber: data.unitNumber,
        templateKey: data.resolved.templateKey,
        templateVersion: data.resolved.templateVersion,
        locale: currentLocale(),
        startedAt: new Date().toISOString(),
        items: {},
        gps: await currentPosition(),
        updatedAt: new Date().toISOString(),
      });
    })();
  }, [data, unitId, items]);

  // Черновик пишется на каждое изменение: телефон садится без предупреждения.
  useEffect(() => {
    if (draft !== null) void saveDraft(draft);
  }, [draft]);

  const item = items[index];
  const current = draft?.items[item?.id ?? ''];

  useEffect(() => {
    setRaw(current?.measurementValue === null || current === undefined
      ? ''
      : String(current.measurementValue));
  }, [index, current]);

  if (data === undefined || draft === null || item === undefined) {
    return (
      <div className="pf-scroll">
        <Text>{t('common.loading')}</Text>
      </div>
    );
  }

  /**
   * Правка идёт от актуального состояния, а не от копии из замыкания.
   *
   * Координаты снимка ждут до четырёх секунд, и всё это время обработчик
   * держит черновик, каким тот был в момент нажатия. Заметка, набранная
   * за эти секунды, затиралась бы приходом фотографии — и наоборот.
   *
   * Запись на диск — отдельным эффектом: обновление состояния обязано быть
   * чистым, иначе StrictMode выполнит сохранение дважды.
   */
  const patch = (changes: Partial<ItemDraft>): void => {
    setDraft((prev) => {
      if (prev === null) return prev;
      const base = prev.items[item.id] ?? emptyItem(item.id);
      const next: InspectionDraft = {
        ...prev,
        items: {
          ...prev.items,
          [item.id]: { ...base, ...changes, capturedAt: new Date().toISOString() },
        },
      };
      return next;
    });
  };

  const label = item.labels[currentLocale()] ?? item.itemKey;
  const doneCount = items.filter((x) => isFilled(x, draft.items[x.id])).length;
  const allFilled = doneCount === items.length;

  const verdict =
    item.type === 'measurement' && item.measurementKind !== null && raw !== ''
      ? evaluateMeasurement(item.measurementKind, Number(raw), item.threshold)
      : null;

  const finish = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const clientUuid = crypto.randomUUID();
      await enqueue({
        clientUuid,
        draftId: draft.draftId,
        unitNumber: draft.unitNumber,
        payload: {
          clientUuid,
          unitId: draft.unitId,
          templateKey: draft.templateKey,
          templateVersion: draft.templateVersion,
          locale: draft.locale,
          startedAtClient: new Date(draft.startedAt),
          submittedAtClient: new Date(),
          gps: draft.gps,
          items: items.map((x) => {
            const value = draft.items[x.id] ?? emptyItem(x.id);
            return {
              id: x.id,
              skipped: value.skipped,
              boolValue: value.boolValue,
              measurementValue: value.measurementValue,
              note: value.note,
              defectReported: value.defectReported,
              capturedAtClient: new Date(value.capturedAt),
              gps: value.gps,
              photoLocalIds: value.photoIds,
              audioLocalId: value.audioId,
            };
          }),
        },
        attempts: 0,
        lastError: null,
        createdAt: new Date().toISOString(),
      });

      // Отправка идёт через очередь: связи может не быть прямо сейчас.
      void runSync();
      void navigate('/done');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pf-screen">
      <div className="pf-scroll">
        <Stack gap="md" maw={640} mx="auto" w="100%">
          <Group justify="space-between" align="center">
            <Text c="dimmed">{draft.unitNumber}</Text>
            <Text c="dimmed">{t('inspection.progress', { done: doneCount, total: items.length })}</Text>
          </Group>
          <Progress value={(doneCount / items.length) * 100} size="lg" radius="xl" />

          <Title order={2}>{label}</Title>
          {item.positionLabel !== null && (
            <Text fz="lg" c="dimmed">
              {item.positionLabel}
            </Text>
          )}

          {item.threshold?.critical !== null && item.threshold !== null && (
            <Badge size="lg" variant="light" color="blue">
              {t('inspection.limit', {
                value: item.threshold.critical,
                unit: item.measurementUnit ?? '',
              })}
            </Badge>
          )}

          {verdict !== null && (
            <Alert
              color={verdict === 'critical' ? 'red' : verdict === 'warn' ? 'orange' : 'blue'}
              variant="light"
            >
              {verdict === 'critical'
                ? t('inspection.verdictCritical')
                : verdict === 'warn'
                  ? t('inspection.verdictWarn')
                  : t('inspection.verdictOk')}
            </Alert>
          )}

          {item.type === 'measurement' && (
            <Card>
              <NumberPad
                value={raw}
                allowDecimal={item.measurementKind === 'pushrod_stroke_in'}
                suffix={item.measurementUnit ?? ''}
                onChange={(value) => {
                  setRaw(value);
                  patch({ measurementValue: value === '' ? null : Number(value) });
                }}
              />
            </Card>
          )}

          {item.type === 'boolean' && (
            <Group grow>
              <Button
                variant={current?.boolValue === true ? 'filled' : 'default'}
                color="blue"
                onClick={() => patch({ boolValue: true, defectReported: false })}
              >
                {t('inspection.ok')}
              </Button>
              <Button
                variant={current?.boolValue === false ? 'filled' : 'default'}
                color="red"
                onClick={() => patch({ boolValue: false, defectReported: true })}
              >
                {t('inspection.defect')}
              </Button>
            </Group>
          )}

          <PhotoCapture
            draftId={draft.draftId}
            photoIds={current?.photoIds ?? []}
            required={item.photoRequired}
            onChange={(photoIds, gps) => patch({ photoIds, ...(gps === null ? {} : { gps }) })}
          />

          <Textarea
            label={t('inspection.note')}
            autosize
            minRows={2}
            value={current?.note ?? ''}
            onChange={(e) => patch({ note: e.currentTarget.value || null })}
          />

          {/* Диктовать удобнее, чем печатать в перчатках: требование CLAUDE.md. */}
          <VoiceNote
            draftId={draft.draftId}
            audioId={current?.audioId ?? null}
            onChange={(audioId) => patch({ audioId })}
          />
        </Stack>
      </div>

      <div className="pf-action-bar">
        <Group grow gap="sm" wrap="nowrap">
          <Button variant="default" disabled={index === 0} onClick={() => setIndex(index - 1)}>
            {t('inspection.back')}
          </Button>
          {index < items.length - 1 ? (
            <Button onClick={() => setIndex(index + 1)}>{t('inspection.next')}</Button>
          ) : (
            <Button
              color="blue"
              disabled={!allFilled}
              loading={submitting}
              onClick={() => void finish()}
            >
              {t('inspection.finish')}
            </Button>
          )}
        </Group>
        {!allFilled && index === items.length - 1 && (
          <Text c="dimmed" fz="sm" mt="xs" ta="center">
            {t('inspection.incomplete')}
          </Text>
        )}
      </div>
    </div>
  );
}
